/**
 * 需求发布管理模块 - 完整的CRUD功能
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, message, Form, Input, Space, Select, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import demandService, { DemandItem } from '../../../../../services/api/demandService';
import { PAGINATION_CONFIG } from '../../components/PaginationConfig';
import QueryArea, { QueryField } from '../../components/QueryArea';
import { DictSelect } from '../../../../../components/common/DictSelect';
import { DICT_TYPES } from '../../../../../constants/dictTypes';
import { useDict } from '../../../../../hooks/useUnifiedDict';

const { TextArea } = Input;

const DemandManagement: React.FC = () => {
  const [dataSource, setDataSource] = useState<DemandItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    demandType: '',
    category: '',
    status: '',
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DemandItem | null>(null);

  // 使用数据字典
  const { getLabelByValue: getCategoryLabel } = useDict(DICT_TYPES.SHOWCASE_CATEGORY);
  const { getLabelByValue: getIndustryLabel } = useDict(DICT_TYPES.SHOWCASE_INDUSTRY);
  const { getLabelByValue: getTechnologyLabel } = useDict(DICT_TYPES.SHOWCASE_TECHNOLOGY);

  // 查询字段配置
  const queryFields: QueryField[] = [
    {
      key: 'keyword',
      label: '关键词',
      type: 'input',
      placeholder: '搜索标题、描述、企业名称...',
      width: 200,
    },
    {
      key: 'demandType',
      label: '需求类型',
      type: 'select',
      options: [
        { label: '全部', value: '' },
        { label: '供应', value: 'supply' },
        { label: '需求', value: 'demand' },
      ],
      placeholder: '选择需求类型',
      width: 120,
    },
    {
      key: 'category',
      label: '分类',
      type: 'dict',
      dictType: DICT_TYPES.SHOWCASE_CATEGORY,
      placeholder: '选择分类',
      width: 150,
    },
    {
      key: 'status',
      label: '状态',
      type: 'select',
      options: [
        { label: '全部', value: '' },
        { label: '待审核', value: '0' },
        { label: '已发布', value: '1' },
        { label: '已下架', value: '2' },
      ],
      placeholder: '选择状态',
      width: 120,
    },
  ];

  // 表格列定义
  const columns: ColumnsType<DemandItem> = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '需求类型',
      dataIndex: 'demandType',
      key: 'demandType',
      width: 100,
      align: 'center',
      render: (value) => (
        <Tag color={value === 'supply' ? 'blue' : 'green'}>
          {value === 'supply' ? '供应' : '需求'}
        </Tag>
      ),
    },
    {
      title: '企业名称',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 180,
      ellipsis: true,
    },
    {
      title: '联系人',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      width: 100,
    },
    {
      title: '联系电话',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      width: 130,
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {record.status === 0 && (
            <Button
              type="link"
              size="small"
              icon={<CheckOutlined />}
              onClick={() => handlePublish(record.id)}
            >
              发布
            </Button>
          )}
          {record.status === 1 && (
            <Button
              type="link"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => handleOffline(record.id)}
            >
              下架
            </Button>
          )}
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 加载数据
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await demandService.getList({
        current,
        size: pageSize,
        keyword: searchParams.keyword || undefined,
        demandType: searchParams.demandType || undefined,
        category: searchParams.category || undefined,
        status: searchParams.status || undefined,
      });
      
      if (response.code === 200 || response.code === 0) {
        const data = response.data;
        let records: DemandItem[] = [];
        let totalCount = 0;
        
        if (data?.records && Array.isArray(data.records)) {
          records = data.records;
          totalCount = data.total || data.records.length;
        } else if (data?.rows && Array.isArray(data.rows)) {
          records = data.rows;
          totalCount = data.total || data.rows.length;
        } else if (Array.isArray(data)) {
          records = data;
          totalCount = data.length;
        }
        
        setDataSource(records);
        setTotal(totalCount);
      } else {
        message.error(response.message || '加载数据失败');
      }
    } catch (error: any) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, searchParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 处理新增
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  // 处理编辑
  const handleEdit = (record: DemandItem) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
    });
    setIsModalVisible(true);
  };

  // 处理删除
  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这条需求吗？',
      onOk: async () => {
        try {
          const response = await demandService.delete(id);
          if (response.code === 200 || response.code === 0) {
            message.success('删除成功');
            loadData();
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error: any) {
          console.error('删除失败:', error);
          message.error('删除失败: ' + (error.message || '未知错误'));
        }
      },
    });
  };

  // 处理发布
  const handlePublish = async (id: number) => {
    try {
      const response = await demandService.publish(id);
      if (response.code === 200 || response.code === 0) {
        message.success('发布成功');
        loadData();
      } else {
        message.error(response.message || '发布失败');
      }
    } catch (error: any) {
      console.error('发布失败:', error);
      message.error('发布失败: ' + (error.message || '未知错误'));
    }
  };

  // 处理下架
  const handleOffline = async (id: number) => {
    try {
      const response = await demandService.offline(id);
      if (response.code === 200 || response.code === 0) {
        message.success('下架成功');
        loadData();
      } else {
        message.error(response.message || '下架失败');
      }
    } catch (error: any) {
      console.error('下架失败:', error);
      message.error('下架失败: ' + (error.message || '未知错误'));
    }
  };

  // 处理查询参数变化 - 自动触发搜索
  const handleSearchParamsChange = useCallback((values: Record<string, any>) => {
    setSearchParams({
      keyword: values.keyword || '',
      demandType: values.demandType || '',
      category: values.category || '',
      status: values.status || '',
    });
    setCurrent(1);
  }, []);

  // 处理保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const demandData: Partial<DemandItem> = {
        ...values,
        status: values.status !== undefined ? values.status : 0,
      };
      
      let response;
      if (editingRecord) {
        response = await demandService.update({ ...editingRecord, ...demandData });
      } else {
        response = await demandService.create(demandData);
      }
      
      if (response.code === 200 || response.code === 0) {
        message.success(editingRecord ? '更新成功' : '创建成功');
        setIsModalVisible(false);
        form.resetFields();
        loadData();
      } else {
        message.error(response.message || '保存失败');
      }
    } catch (error: any) {
      console.error('保存失败:', error);
      if (error.errorFields) {
        message.error('请检查表单填写是否正确');
      } else {
        message.error('保存失败: ' + (error.message || '未知错误'));
      }
    }
  };

  // 处理批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的数据');
      return;
    }
    
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条数据吗？`,
      onOk: async () => {
        try {
          const response = await demandService.batchDelete(selectedRowKeys as number[]);
          if (response.code === 200 || response.code === 0) {
            message.success('批量删除成功');
            setSelectedRowKeys([]);
            loadData();
          } else {
            message.error(response.message || '批量删除失败');
          }
        } catch (error: any) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败: ' + (error.message || '未知错误'));
        }
      },
    });
  };

  return (
    <div className="demand-management">
      <QueryArea
        fields={queryFields}
        values={searchParams}
        onValuesChange={handleSearchParamsChange}
        onAdd={handleAdd}
        onBatchDelete={handleBatchDelete}
        batchDeleteDisabled={selectedRowKeys.length === 0}
        selectedCount={selectedRowKeys.length}
        addButtonText="新增"
        batchDeleteButtonText="批量删除"
      />

      {/* 数据表格 */}
      <div className="table-card">
        <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowKey="id"
        pagination={{
          current,
          pageSize,
          total,
          ...PAGINATION_CONFIG.simple,
          onChange: (page, size) => {
            setCurrent(page);
            setPageSize(size);
          },
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        />
      </div>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑需求' : '新增需求'}
        open={isModalVisible}
        onOk={handleSave}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        width={900}
        confirmLoading={loading}
        okText="确定"
        cancelText="取消"
        destroyOnClose
      >
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
          labelAlign="left"
          initialValues={{
            demandType: 'demand',
            status: 0,
          }}
        >
          <Form.Item
            name="title"
            label="需求标题"
            rules={[{ required: true, message: '请输入需求标题' }]}
          >
            <Input placeholder="请输入需求标题" />
          </Form.Item>

          <Form.Item
            name="description"
            label="需求描述"
            rules={[{ required: true, message: '请输入需求描述' }]}
          >
            <TextArea rows={4} placeholder="请输入需求描述" />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="demandType"
              label="需求类型"
              rules={[{ required: true, message: '请选择需求类型' }]}
              style={{ flex: 1 }}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Select placeholder="请选择需求类型">
                <Select.Option value="supply">供应</Select.Option>
                <Select.Option value="demand">需求</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="category"
              label="需求分类"
              style={{ flex: 1 }}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <DictSelect
                dictType={DICT_TYPES.SHOWCASE_CATEGORY}
                placeholder="请选择需求分类"
              />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="industry"
              label="所属行业"
              style={{ flex: 1 }}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <DictSelect
                dictType={DICT_TYPES.SHOWCASE_INDUSTRY}
                placeholder="请选择所属行业"
              />
            </Form.Item>

            <Form.Item
              name="technology"
              label="核心技术"
              style={{ flex: 1 }}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <DictSelect
                dictType={DICT_TYPES.SHOWCASE_TECHNOLOGY}
                placeholder="请选择核心技术"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="companyName"
            label="企业名称"
          >
            <Input placeholder="请输入企业名称" />
          </Form.Item>

          <Form.Item
            name="contactPerson"
            label="联系人"
          >
            <Input placeholder="请输入联系人" />
          </Form.Item>

          <Form.Item
            name="contactPhone"
            label="联系电话"
          >
            <Input placeholder="请输入联系电话" />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="contactEmail"
              label="联系邮箱"
              style={{ flex: 1 }}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Input placeholder="请输入联系邮箱" />
            </Form.Item>

            <Form.Item
              name="status"
              label="状态"
              style={{ flex: 1 }}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 18 }}
            >
              <Select placeholder="请选择状态">
                <Select.Option value={0}>待审核</Select.Option>
                <Select.Option value={1}>已发布</Select.Option>
                <Select.Option value={2}>已下架</Select.Option>
              </Select>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default DemandManagement;

